import React, { useCallback, useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Copy, Send, Check, RotateCcw, MessageSquare } from "lucide-react";

/**
 * Tap-to-Send SMS panel — admin-only.
 * Renders the three reminder buckets (72h / 24h / 1h) with:
 *   - the pre-filled message body
 *   - "Open SMS" → triggers `sms:` URI on phone (native handoff)
 *   - "Copy" → copies message to clipboard
 *   - "Mark as sent" / "Undo" → flips the per-bucket sent flag on the backend
 *
 * 72h is hidden for consultation appointments (deposit-policy-only).
 */
export default function SmsTapToSend({ appointmentId, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/admin/appointments/${appointmentId}/sms-status`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [appointmentId]);
  useEffect(() => { if (appointmentId) load(); }, [appointmentId, load]);

  const markSent = async (bucket) => {
    setBusy(`mark-${bucket}`);
    try {
      await http.post(`/admin/appointments/${appointmentId}/sms-marked`, { bucket });
      toast.success(`Marked ${bucket} reminder as sent`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(null); }
  };

  const clearSent = async (bucket) => {
    setBusy(`clear-${bucket}`);
    try {
      await http.post(`/admin/appointments/${appointmentId}/sms-clear`, { bucket });
      toast.success(`Cleared ${bucket} reminder`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(null); }
  };

  const copy = async (msg) => {
    try {
      await navigator.clipboard.writeText(msg);
      toast.success("Message copied");
    } catch (_e) {
      toast.error("Could not copy. Long-press to copy manually.");
    }
  };

  if (!data) {
    return (
      <div className="frame-card p-5 flex items-center gap-2 text-sm text-[#A3A8A4]" data-testid="sms-tts-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#D2A853]" /> Loading reminders…
      </div>
    );
  }
  const isConsultation = (data.appointment_type || "").toLowerCase() === "consultation";
  const phone = data.phone_uri || "";
  const hasPhone = !!phone;
  const buckets = (data.buckets || []).filter((b) => !(isConsultation && b.bucket === "72h"));

  return (
    <div className="space-y-3" data-testid="sms-tts-panel">
      {!hasPhone && (
        <p className="text-xs text-[#CF6679]" data-testid="sms-tts-nophone">
          No phone number on file for this client — Tap-to-Send unavailable.
        </p>
      )}
      {buckets.map((b) => {
        const sent = !!b.sent_at;
        const smsHref = hasPhone
          ? `sms:${phone}${/iPhone|iPad|iPod/i.test(navigator.userAgent || "") ? "&" : "?"}body=${encodeURIComponent(b.message)}`
          : null;
        return (
          <div
            key={b.bucket}
            className={`frame-card p-4 ${sent ? "opacity-70" : ""}`}
            data-testid={`sms-tts-${b.bucket}`}
          >
            <div className="flex items-center justify-between gap-3 mb-3">
              <span className="overline text-[#D2A853]">{labelFor(b.bucket)}</span>
              {sent && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#A3A8A4]" data-testid={`sms-tts-sent-${b.bucket}`}>
                  <Check className="w-3 h-3" /> Sent {timeAgo(b.sent_at)}
                </span>
              )}
            </div>
            <div className="bg-[#0A120E] border border-[#D2A853]/15 p-3 text-sm text-[#E8E9E3] leading-relaxed mb-3 whitespace-pre-wrap">
              {b.message}
            </div>
            <div className="flex flex-wrap gap-2">
              {smsHref && (
                <a
                  href={smsHref}
                  className="btn-brass-sm inline-flex items-center gap-2"
                  data-testid={`sms-tts-open-${b.bucket}`}
                >
                  <Send className="w-3.5 h-3.5" /> Open SMS
                </a>
              )}
              <button
                onClick={() => copy(b.message)}
                className="btn-ghost-sm inline-flex items-center gap-2"
                data-testid={`sms-tts-copy-${b.bucket}`}
              >
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
              {sent ? (
                <button
                  onClick={() => clearSent(b.bucket)}
                  disabled={busy === `clear-${b.bucket}`}
                  className="btn-ghost-sm inline-flex items-center gap-2"
                  data-testid={`sms-tts-undo-${b.bucket}`}
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Undo
                </button>
              ) : (
                <button
                  onClick={() => markSent(b.bucket)}
                  disabled={busy === `mark-${b.bucket}`}
                  className="btn-ghost-sm inline-flex items-center gap-2 text-[#D2A853] border-[#D2A853]/40 hover:border-[#D2A853]"
                  data-testid={`sms-tts-mark-${b.bucket}`}
                >
                  {busy === `mark-${b.bucket}` ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <MessageSquare className="w-3.5 h-3.5" />
                  )}
                  Mark as sent
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function labelFor(bucket) {
  if (bucket === "72h") return "72-Hour Reminder · deposit policy";
  if (bucket === "24h") return "24-Hour Reminder · tomorrow";
  return "1-Hour Reminder · pre-arrival";
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}
